import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Trash2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, toastErr } from "@/lib/api";
import { Card } from "./ui";

const StatusBadge = ({ d }) => {
  const base = "text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap";
  if (d.mode === "always") return <span data-testid="knowledge-status" className={`${base} bg-slate-100 text-slate-600`}>Always included</span>;
  if (d.status === "ready") return <span data-testid="knowledge-status" className={`${base} bg-emerald-50 text-emerald-700`}>Ready · {d.chunks} chunks</span>;
  if (d.status === "indexing") return <span data-testid="knowledge-status" className={`${base} bg-indigo-50 text-indigo-700 flex items-center gap-1`}><Loader2 size={11} className="animate-spin" /> Indexing {d.chunks || 0}</span>;
  if (d.status === "error") return <span data-testid="knowledge-status" className={`${base} bg-red-50 text-red-700`} title={d.error}>Error</span>;
  return <span data-testid="knowledge-status" className={`${base} bg-amber-50 text-amber-700`}>Not indexed</span>;
};

export const KnowledgeTab = () => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/knowledge").then((r) => setDocs(r.data)).catch(toastErr);
  useEffect(() => { load(); }, []);
  const indexing = docs.some((d) => d.status === "indexing");
  useEffect(() => {
    if (!indexing) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [indexing]);

  const reindex = async (d) => {
    try { await api.post(`/admin/knowledge/${d.id}/reindex`); toast.success(`Indexing ${d.name}…`); load(); }
    catch (e) { toastErr(e); }
  };

  const upload = async (files) => {
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post("/admin/knowledge", fd); toast.success(`Added ${f.name}`); }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setUploading(false);
    load();
  };
  const remove = async (d) => {
    try { await api.delete(`/admin/knowledge/${d.id}`); toast.success(`Removed ${d.name}`); load(); }
    catch (e) { toastErr(e); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }}
          onClick={() => fileRef.current.click()}
          className="border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 rounded-xl p-8 text-center cursor-pointer transition-colors"
        >
          <input ref={fileRef} data-testid="knowledge-file-upload-input" type="file" multiple accept=".txt,.md,.pdf,.docx" className="hidden" onChange={(e) => upload([...e.target.files])} />
          {uploading ? <Loader2 className="mx-auto animate-spin text-indigo-500" /> : <Upload className="mx-auto text-slate-400" />}
          <p className="mt-3 text-sm font-medium text-slate-700">Drop files here or click to upload</p>
          <p className="text-xs text-slate-500 mt-1">.txt, .md, .pdf, .docx · up to 10MB · small docs (≤15k chars) are always in the prompt; large docs are chunked, embedded and searched per question</p>
        </div>
      </Card>
      <Card title={`Documents (${docs.length})`}>
        {docs.length === 0 && <p data-testid="knowledge-empty" className="text-sm text-slate-500">No documents yet.</p>}
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id} data-testid="knowledge-file-item" className="flex items-center gap-3 py-3">
              <FileText size={18} className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
                <p className="text-xs text-slate-500">{d.chars.toLocaleString()} chars · {new Date(d.created_at).toLocaleDateString()}</p>
                {d.status === "error" && <p data-testid="knowledge-error" className="text-xs text-red-600 mt-0.5">{d.error}</p>}
              </div>
              <StatusBadge d={d} />
              {d.mode === "retrieval" && d.status !== "indexing" && (
                <button data-testid="knowledge-reindex-button" onClick={() => reindex(d)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" aria-label="Re-index" title="Re-index"><RefreshCw size={16} /></button>
              )}
              <button data-testid="knowledge-delete-button" onClick={() => remove(d)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" aria-label="Delete"><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};
